import { DeliveryZoneCodeId } from '@noths/polaris-client-localisation';
import { Image, Placement } from '@noths/polaris-client-ribbons-design-system';
import { NonNullableKeys } from '@noths/polaris-dev-ts-recipes';
import { DeliveryByEvent, Link, Navigation, Price } from '@noths/polaris-dev-ts-types';
import { SerializedError } from '@reduxjs/toolkit';

import { Partner } from '.';
import { DeliveryOptionCode } from './deliveryZoneOptions';

export const starRatings = [1, 2, 3, 4, 5] as const;

export type StarRating = typeof starRatings[number] | 0 | null;

export type InventoryStatus = 'in_stock' | 'made_to_order' | 'out_of_stock';

export type Availability = 'available' | 'discontinued' | 'suspended' | 'quarantined';

export type AllowedCase = null | 'mixed' | 'not applicable' | 'upper' | 'lower';

export type ProductOptionType = 'selection' | 'personalisation';
export interface PriceModifier {
  type: 'fixed' | 'percentage';
  amount?: number;
  prices: Price[];
}

export interface ProductOptionValue {
  id: number;
  name: string;
  priceModifier: null | PriceModifier;
  isCustomInput?: boolean;
  isValid?: boolean;
}

export interface DeliveryOption {
  zone: { id: DeliveryZoneCodeId; name: string; shortName: string; international: boolean };
  service: { name: string; code: DeliveryOptionCode; express: boolean };
  prices: Price[];
}

export interface ProductOption {
  id: number;
  name: string;
  type: ProductOptionType;
  required: boolean;
  minCharacters: null | number;
  maxCharacters: null | number;
  allowedCase: AllowedCase;
  values: null | ProductOptionValue[];
}

export type SelectionProductOption = NonNullableKeys<ProductOption, 'values'>;
export type PersonalisationProductOption = NonNullableKeys<
  ProductOption,
  'minCharacters' | 'maxCharacters' | 'allowedCase'
>;

export interface ProductSpecialFeatures {
  madeInBritain: boolean;
  personalisable: boolean;
  ecoFriendly: boolean;
  exclusive: boolean;
}

export interface Product {
  allowsGiftMessage: boolean;
  allowsGiftReceipt: boolean;
  allowsGiftWrap: boolean;
  availability: Availability;
  available: boolean;
  backInStockAt: null | string;
  breadcrumbCategoryId: null | string;
  code: number;
  deliveryOptions: null | DeliveryOption[];
  description: string;
  dimensions: string;
  deliveryByEvent: null | DeliveryByEvent;
  freeDomesticDelivery: boolean;
  freeGiftWrap: boolean;
  images: Image[];
  inventoryStatus: InventoryStatus;
  isNew: boolean;
  links: Link[];
  materials: string;
  navigation: Navigation[];
  numberOfReviews: number;
  onSale: boolean;
  options: ProductOption[];
  ingredients: Link | null;
  otherInformation: string;
  partner: Partner;
  preSalePrices: Price[] | null;
  price: Price;
  prices: Price[];
  productRating: StarRating;
  proposedProductTypeId: number;
  purchasable: boolean;
  returnsInformation: string;
  salePercentage: null | number;
  shippingInfo: string | null;
  specialFeatures: ProductSpecialFeatures;
  title: string;
  variationsAvailable: string;
}

export type SimilarProduct = Pick<
  Product,
  'availability' | 'code' | 'images' | 'links' | 'partner' | 'purchasable' | 'title'
> & {
  new: boolean;
  on_sale: boolean;
  free_domestic_delivery: boolean;
  prices: Price[];
  sale_percentage?: number;
  pre_sale_prices?: Price[];
  inventory_status?: InventoryStatus;
  linkURL: string;
};

export type ProductPlacementRecord = {
  products: SimilarProduct[];
  placementStrategy?: string;
  placementType?: Placement;
  placementTitle?: string;
};

export type ProductPlacementRecordError = Pick<ProductPlacementRecord, 'placementType'> & {
  error: SerializedError;
};
